// Why the back refused a scope file, in Spanish.
//
// back-ratings answers `POST users/scopes` with a list since its #46 (its
// handoff of 2026-09-25): {"errors": [{field, code, message, params}]}, one
// entry per problem, so a file with a bad hour and a bad cost says both. A
// column of the uploaded workbook is prefixed with `template.`, which is what
// keeps it apart from a field of the form.
//
// The old shape, a map of field to English sentences, was read here while
// that change waited on this reader; it went when the list reached the
// back's production on 2026-09-25 (back-ratings #46, revision
// back-ratings-service-00552-sp4).
//
// `message` in an entry is English and for diagnosis; the client reads the
// Spanish below, which is this repository's.

// What each field is called in a sentence a client reads.
const FIELDS = {
  type: 'el tipo de evaluación',
  email: 'el correo',
  template: 'el archivo',
  lower_date: 'la fecha inicial',
  upper_date: 'la fecha final',
  custom_target: 'el target personalizado',
  'template.fecha': 'la fecha',
  'template.hora': 'la hora',
  'template.duracion': 'la duración',
  'template.costo': 'el costo',
  'template.n_spots': 'la cantidad de spots',
  'template.nombre_canal': 'el nombre de canal',
  'template.dia_semana': 'el día de la semana',
  'template.name': 'el nombre de programa',
};

const named = (field) => FIELDS[field] || 'un campo del formulario';

// "de el costo" is not Spanish: a label carries its article, so the
// contraction is made here rather than in every sentence.
const of = (field) => {
  const label = named(field);
  return label.startsWith('el ') ? `del ${label.slice(3)}` : `de ${label}`;
};

const listOf = (params, key) => {
  const list = params && params[key];
  return Array.isArray(list) && list.length > 0 ? list : null;
};

const options = (params) => {
  const list = listOf(params, 'options');
  return list ? ` (opciones: ${list.join(', ')})` : '';
};

// What the client wrote that the back did not recognise: the channels of the
// workbook, or the targets typed by hand. Saying which ones is the whole
// point — before 2026-09-28 the portal could only say that one of them was
// wrong, and the client had to find it (back-ratings #56).
const unknown = (params) => {
  const list = listOf(params, 'unknown');
  return list ? `: ${list.join(', ')}` : '';
};

// One sentence per problem. An unknown code is not invented away: it says
// something true and general, so a code the back adds before this table
// knows it still reaches the client as a refusal.
const sentence = ({ field, code, params }) => {
  switch (code) {
    case 'field_required':
      return `Falta ${named(field)}. Revisa e intenta nuevamente.`;
    case 'field_invalid_value':
      return `El valor ${of(field)} no es válido${options(params)}. Revisa e intenta nuevamente.`;
    case 'field_invalid_format':
      return `El formato ${of(field)} es incorrecto. Revisa e intenta nuevamente.`;
    case 'custom_target_out_of_range':
      // Its params carry the 24 targets that are allowed as well, which is
      // too long to read in an alert and not what was asked.
      return `El target personalizado no existe${unknown(params)}. Revisa e intenta nuevamente.`;
    case 'template_invalid_format':
      return 'La información del archivo cargado no es válida. Usa el template';
    case 'template_no_valid_spots':
      return 'El archivo no tiene spots. Revisa e intenta nuevamente.';
    case 'channel_unknown':
      return `Ha ingresado un nombre de canal incorrecto${unknown(params)}. Revisa e intenta nuevamente.`;
    case 'weekday_unknown':
      return `Ha ingresado un día de la semana incorrecto${options(params)}. Revisa e intenta nuevamente.`;
    case 'program_unknown':
      return "Ha ingresado un nombre de programa incorrecto. Revisa el archivo 'error_names.csv'.";
    default:
      return `No fue posible cargar el archivo por ${named(field)}. Revisa e intenta nuevamente.`;
  }
};

const entriesOf = (answer) => {
  const data = answer && answer.data;
  return data && Array.isArray(data.errors) ? data.errors.filter((e) => e && e.code) : [];
};

// What to tell the client, and the rows of the programmes that were not
// found, which the caller writes to error_names.csv as it always has.
export const scopeRefusal = (answer) => {
  const entries = entriesOf(answer);
  const messages = [...new Set(entries.map(sentence))];
  const unknownPrograms = entries
    .filter((entry) => entry.code === 'program_unknown')
    .map((entry) => (entry.params || {}).names)
    .find((names) => Array.isArray(names) && names.length > 0) || null;

  return { messages, unknownPrograms };
};
